import {
  Outlet,
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
  redirect,
} from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import { activeMembership, sessionStore } from './api';
import { Toaster } from './components/ui';
import { NotFound, PagePending } from './components/fallbacks';
import { queryClient } from './lib/queryClient';
import { isStaffRole } from './lib/session';

// ---------- search param helpers ----------
const str = (v: unknown): string | undefined => (typeof v === 'string' && v.length > 0 ? v : undefined);
const date = (v: unknown): string | undefined => {
  const x = str(v);
  return x && /^\d{4}-\d{2}-\d{2}$/.test(x) ? x : undefined;
};
/** Search params are all optional so links needn't pass them. */
const opt = <T extends object>(o: T): Partial<T> => o;
const oneOf = <T extends string>(v: unknown, allowed: readonly T[]): T | undefined =>
  allowed.includes(v as T) ? (v as T) : undefined;

// ---------- root ----------
const rootRoute = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  component: () => (
    <>
      <Outlet />
      <Toaster />
    </>
  ),
  notFoundComponent: NotFound,
});

// ---------- public (no login) ----------
const publicCardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/c/$token',
  component: lazyRouteComponent(() => import('./pages/public/PublicCardPage'), 'PublicCardPage'),
});
const publicConsentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/consent/$token',
  component: lazyRouteComponent(() => import('./pages/public/PublicConsentPage'), 'PublicConsentPage'),
});
const mandateDoneRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/billing/mandate-done',
  validateSearch: (s: Record<string, unknown>) => opt({ token: str(s.token) }),
  component: lazyRouteComponent(() => import('./pages/public/MandateDonePage'), 'MandateDonePage'),
});

const publicPayRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/pay/$token',
  validateSearch: (s: Record<string, unknown>) => opt({ paid: s.paid === '1' || s.paid === 1 || s.paid === true ? true : undefined }),
  component: lazyRouteComponent(() => import('./pages/public/PublicPayPage'), 'PublicPayPage'),
});

// ---------- auth ----------
const redirectIfSignedIn = () => {
  const s = sessionStore.get();
  if (s) throw redirect({ to: isStaffRole(activeMembership(s)) ? '/' : '/me' });
};
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: (s: Record<string, unknown>) => opt({ redirect: str(s.redirect) }),
  beforeLoad: redirectIfSignedIn,
  component: lazyRouteComponent(() => import('./pages/auth/LoginPage'), 'LoginPage'),
});
const registerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/register',
  beforeLoad: redirectIfSignedIn,
  component: lazyRouteComponent(() => import('./pages/auth/RegisterPage'), 'RegisterPage'),
});
const magicRequestRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login/link',
  validateSearch: (s: Record<string, unknown>) => opt({ email: str(s.email) }),
  component: lazyRouteComponent(() => import('./pages/auth/MagicLinkPage'), 'MagicLinkPage'),
});
const magicVerifyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/magic',
  validateSearch: (s: Record<string, unknown>) => opt({ token: str(s.token) }),
  component: lazyRouteComponent(() => import('./pages/auth/MagicVerifyPage'), 'MagicVerifyPage'),
});

// ---------- owner (client) view ----------
const ownerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/me',
  beforeLoad: ({ location }) => {
    if (!sessionStore.get()) throw redirect({ to: '/login', search: { redirect: location.href } });
  },
  validateSearch: (s: Record<string, unknown>) => opt({ tab: oneOf(s.tab, ['feed', 'billing', 'forms'] as const) }),
  component: lazyRouteComponent(() => import('./pages/owner/OwnerPage'), 'OwnerPage'),
});

// ---------- business dashboard ----------
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_app',
  beforeLoad: ({ location }) => {
    const s = sessionStore.get();
    if (!s) throw redirect({ to: '/login', search: { redirect: location.href } });
    if (!isStaffRole(activeMembership(s))) {
      const staff = s.user.memberships.find(isStaffRole);
      if (staff) sessionStore.set({ ...s, activeBusinessId: staff.businessId });
      else throw redirect({ to: '/me' });
    }
  },
  component: lazyRouteComponent(() => import('./pages/app/AppShell'), 'AppShell'),
  pendingComponent: PagePending,
});

const page = <T extends string>(path: T, loader: () => Promise<Record<string, unknown>>, name: string) =>
  createRoute({
    getParentRoute: () => appRoute,
    path,
    component: lazyRouteComponent(loader as never, name as never),
    pendingComponent: PagePending,
  });

const homeRoute = page('/', () => import('./pages/app/HomePage'), 'HomePage');
const scheduleRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'schedule',
  validateSearch: (s: Record<string, unknown>) => opt({ week: date(s.week) }),
  component: lazyRouteComponent(() => import('./pages/app/schedule/SchedulePage'), 'SchedulePage'),
  pendingComponent: PagePending,
});
const clientsRoute = page('clients', () => import('./pages/app/clients/ClientsPage'), 'ClientsPage');
const clientRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'clients/$clientId',
  validateSearch: (s: Record<string, unknown>) => opt({ tab: oneOf(s.tab, ['overview', 'dogs', 'consents', 'billing'] as const) }),
  component: lazyRouteComponent(() => import('./pages/app/clients/ClientDetailPage'), 'ClientDetailPage'),
  pendingComponent: PagePending,
});
const dogsRoute = page('dogs', () => import('./pages/app/dogs/DogsPage'), 'DogsPage');
const dogRoute = page('dogs/$dogId', () => import('./pages/app/dogs/DogDetailPage'), 'DogDetailPage');
const cardsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'cards',
  validateSearch: (s: Record<string, unknown>) => opt({ date: date(s.date), dogId: str(s.dogId), clientId: str(s.clientId) }),
  component: lazyRouteComponent(() => import('./pages/app/cards/CardsPage'), 'CardsPage'),
  pendingComponent: PagePending,
});
const cardRoute = page('cards/$cardId', () => import('./pages/app/cards/CardDetailPage'), 'CardDetailPage');

const recordsIndexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'records',
  beforeLoad: () => {
    throw redirect({ to: '/records/compliance' });
  },
});
const complianceRoute = page('records/compliance', () => import('./pages/app/records/CompliancePage'), 'CompliancePage');
const incidentsRoute = page('records/incidents', () => import('./pages/app/records/IncidentsPage'), 'IncidentsPage');
const incidentRoute = page('records/incidents/$incidentId', () => import('./pages/app/records/IncidentDetailPage'), 'IncidentDetailPage');
const cleaningRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'records/cleaning',
  validateSearch: (s: Record<string, unknown>) => opt({ date: date(s.date) }),
  component: lazyRouteComponent(() => import('./pages/app/records/CleaningPage'), 'CleaningPage'),
  pendingComponent: PagePending,
});
const registerDayRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'records/register',
  validateSearch: (s: Record<string, unknown>) => opt({ date: date(s.date) }),
  component: lazyRouteComponent(() => import('./pages/app/records/RegisterPage'), 'RegisterPage'),
  pendingComponent: PagePending,
});
const consentsRoute = page('records/consents', () => import('./pages/app/records/ConsentsPage'), 'ConsentsPage');
const inspectionRoute = page('records/inspection', () => import('./pages/app/records/InspectionPage'), 'InspectionPage');

const billingRoute = page('billing', () => import('./pages/app/billing/BillingPage'), 'BillingPage');
const invoicesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'billing/invoices',
  validateSearch: (s: Record<string, unknown>) => opt({
    status: oneOf(s.status, ['draft', 'issued', 'submitted', 'paid', 'failed', 'cancelled'] as const),
    clientId: str(s.clientId),
  }),
  component: lazyRouteComponent(() => import('./pages/app/billing/InvoicesPage'), 'InvoicesPage'),
  pendingComponent: PagePending,
});
const invoiceRoute = page('billing/invoices/$invoiceId', () => import('./pages/app/billing/InvoiceDetailPage'), 'InvoiceDetailPage');
const creditsRoute = page('billing/credits', () => import('./pages/app/billing/CreditsPage'), 'CreditsPage');

export const SETTINGS_TABS = ['business', 'billing', 'services', 'team', 'documents', 'cleaning'] as const;
export type SettingsTab = (typeof SETTINGS_TABS)[number];
const settingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'settings',
  validateSearch: (s: Record<string, unknown>) => opt({ tab: oneOf(s.tab, SETTINGS_TABS) }),
  component: lazyRouteComponent(() => import('./pages/app/settings/SettingsPage'), 'SettingsPage'),
  pendingComponent: PagePending,
});

const routeTree = rootRoute.addChildren([
  publicCardRoute,
  publicConsentRoute,
  mandateDoneRoute,
  publicPayRoute,
  loginRoute,
  registerRoute,
  magicRequestRoute,
  magicVerifyRoute,
  ownerRoute,
  appRoute.addChildren([
    homeRoute,
    scheduleRoute,
    clientsRoute,
    clientRoute,
    dogsRoute,
    dogRoute,
    cardsRoute,
    cardRoute,
    recordsIndexRoute,
    complianceRoute,
    incidentsRoute,
    incidentRoute,
    cleaningRoute,
    registerDayRoute,
    consentsRoute,
    inspectionRoute,
    billingRoute,
    invoicesRoute,
    invoiceRoute,
    creditsRoute,
    settingsRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
